import { ActionCellRecord } from '@/features/invoice_reports/components/List/Table/Cells/components/ActionCell/ActionCell';
import type { ActionMenuItems } from '@moneyforward/ap-frontend-components';
import { useMemo } from 'react';

export type UseApplicationListActionMenuOptions = {
  record: ActionCellRecord;
  t: (key: string) => string;
  onDetail?: (reportId: string) => void;
  onDuplicate?: (reportId: string) => void;
  onDelete?: (reportId: string) => void;
  onCancel?: (reportId: string) => void;
};

/**
 * Application list action menu (NA-4165).
 * Driven by record edit_action, delete_action, cancel_action: visible = show, allowed = enabled, reason = tooltip.
 */
export function useApplicationListActionMenu({
  record,
  t,
  onDetail,
  onDuplicate,
  onDelete,
  onCancel,
}: UseApplicationListActionMenuOptions): {
  isEditVisible: boolean;
  isEditDisabled: boolean;
  editHref: string;
  menuItems: ActionMenuItems;
  editDisabledTooltip: string | undefined;
  deleteMenuItemTooltip: string | undefined;
  cancelMenuItemTooltip: string | undefined;
} {
  return useMemo(() => {
    const { edit_action, delete_action, cancel_action } = record;

    const isEditVisible = edit_action.visible;
    const isEditDisabled = !edit_action.allowed;
    const editHref = record.detail_path;
    const editDisabledTooltip = edit_action.reason ?? undefined;
    const deleteMenuItemTooltip = delete_action.allowed
      ? undefined
      : delete_action.reason ?? undefined;
    const cancelMenuItemTooltip = cancel_action.allowed
      ? undefined
      : cancel_action.reason ?? undefined;

    const menuItems: ActionMenuItems = [
      {
        key: 'detail',
        label: t('detail'),
        onClick: () => onDetail?.(record.id),
      },
      {
        key: 'duplicate',
        label: t('duplicate'),
        disabled: !record.is_copyable,
        onClick: () => onDuplicate?.(record.id),
      },
      ...(delete_action.visible
        ? [
            {
              key: 'delete',
              label: t('delete'),
              disabled: !delete_action.allowed,
              onClick: () => onDelete?.(record.id),
            },
          ]
        : []),
      ...(cancel_action.visible
        ? [
            {
              key: 'cancel',
              label: t('ap_report_status_canceled'),
              disabled: !cancel_action.allowed,
              onClick: () => onCancel?.(record.id),
            },
          ]
        : []),
    ];

    return {
      isEditVisible,
      isEditDisabled,
      editHref,
      menuItems,
      editDisabledTooltip,
      deleteMenuItemTooltip,
      cancelMenuItemTooltip,
    };
  }, [record, t, onDetail, onDuplicate, onDelete, onCancel]);
}
